import { type JSX } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { cn } from '../../lib/utils.ts';
import { useRoleIntro } from '../../lib/use-role-intro.ts';
import { SaveError } from '../SaveError.tsx';
import type {
  RoleIntroBeat,
  RoleIntroContent,
  RoleIntroFork,
  RoleIntroStep,
} from '../../lib/role-intro-content.ts';
import { buttonClass, Card } from '../ui/index.ts';

interface BeatTiming {
  /** The connector BEFORE this beat, null for the first beat. */
  connectorDelayMs: number | null;
  chipDelayMs: number;
}

/** How long the longest lane in a fork takes to finish popping in (ForkPanel's own 85ms-per-step stagger). */
function forkDurationMs(fork: RoleIntroFork): number {
  return Math.max(...fork.lanes.map((lane) => lane.steps.length)) * 85;
}

interface Timeline {
  beats: BeatTiming[];
  /** When the fork panel starts drawing — set only when the content has a fork. */
  forkStartDelayMs: number | null;
}

/**
 * The fork RENDERS below the journey row but is TIMED from the beat that announces it, so the
 * eye is already on "↓ two paths in" when the panel draws itself underneath.
 */
function buildTimeline(content: RoleIntroContent): Timeline {
  let delay = 60;
  let forkStartDelayMs: number | null = null;
  const beats = content.beats.map((beat, i) => {
    const connectorDelayMs = i > 0 ? delay : null;
    if (i > 0) delay += 220;
    const chipDelayMs = delay;

    if (beat.expands === true && content.fork !== undefined) {
      forkStartDelayMs = delay + 300;
      delay = forkStartDelayMs + forkDurationMs(content.fork) + 150;
    } else {
      delay += 380;
    }
    return { connectorDelayMs, chipDelayMs };
  });
  return { beats, forkStartDelayMs };
}

/**
 * "Practice Compass" - a fast, self-drawing map of what THIS role does in HapMetrix, shown
 * once the first time a person holds it (useRoleIntro decides which role, if any). Not a
 * product tour over the real screens: this is the "trailer", shown before the person has any
 * screens to point at. The draw is staggered CSS keyframes, no animation library.
 *
 * LAYOUT is the approved prototype's: ONE journey row, sub-flow on the row BELOW, no scroll.
 * TF-0002 was both halves failing - the fork sat INSIDE the non-wrapping row, where its
 * `basis-full` could not wrap, and the row's 24px gaps overflowed the dialog on their own.
 * Measurements here are the prototype's; role-intro-walkthrough.spec.ts holds the proof.
 */
export function RoleIntroOverlay(): JSX.Element | null {
  const intro = useRoleIntro();
  if (intro === null) return null;

  const { content, dismiss, dismissal } = intro;
  const timeline = buildTimeline(content);

  return (
    <Dialog.Root open onOpenChange={(open) => { if (!open) dismiss(); }}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-foreground/50 backdrop-blur-[1px]',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0',
          )}
        />
        <Dialog.Content
          onOpenAutoFocus={(e) => e.preventDefault()}
          className={cn(
            // 66.5rem = the prototype's 1000px stage plus this Card's own padding, so the
            // journey row gets the 938px it measures at and never scrolls sideways.
            'fixed left-1/2 top-1/2 z-50 w-[min(66.5rem,94vw)] -translate-x-1/2 -translate-y-1/2',
            'focus:outline-none',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          )}
        >
          <Card
            data-testid="role-intro-overlay"
            className="max-h-[88vh] overflow-y-auto p-5 sm:p-8"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                  Welcome to HapMetrix
                </p>
                <Dialog.Title className="mt-1 text-xl font-bold tracking-tight text-foreground">
                  {content.title}
                </Dialog.Title>
              </div>
              <Dialog.Close asChild>
                <button
                  type="button"
                  data-testid="role-intro-skip"
                  className={buttonClass('secondary', 'sm')}
                >
                  Skip
                </button>
              </Dialog.Close>
            </div>

            <div className="mt-6 flex flex-col gap-4 rounded-md border border-border bg-background p-4 sm:p-6">
              <div
                data-testid="role-intro-journey"
                className="flex flex-col gap-4 md:flex-row md:items-stretch md:gap-0"
              >
                {content.beats.map((beat, i) => {
                  const timing = timeline.beats[i];
                  return (
                    <div key={beat.label} className="contents">
                      {timing.connectorDelayMs !== null ? (
                        <Connector delayMs={timing.connectorDelayMs} />
                      ) : null}
                      <Beat beat={beat} delayMs={timing.chipDelayMs} />
                    </div>
                  );
                })}
              </div>

              {content.fork !== undefined && timeline.forkStartDelayMs !== null ? (
                <ForkPanel fork={content.fork} startDelayMs={timeline.forkStartDelayMs} />
              ) : null}
            </div>

            <div className="mt-6 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
              <p className="max-w-prose text-sm text-muted-foreground">{content.tagline}</p>
              <button
                type="button"
                data-testid="role-intro-continue"
                onClick={dismiss}
                className={buttonClass('primary')}
              >
                Let’s go
              </button>
            </div>
            <SaveError mutation={dismissal} fallback="Could not save that. Please try again." />
          </Card>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Held 38px below the row's top, where the prototype puts it, rather than centred on the beat:
 * a beat's height varies with how many lines its label wraps to, and centring drifts with it.
 */
function Connector({ delayMs }: { delayMs: number }): JSX.Element {
  return (
    <div
      className={cn(
        'relative overflow-hidden bg-border',
        'ml-[27px] h-5 w-0.5 self-start',
        'md:-ml-0.5 md:-mr-0.5 md:mt-[38px] md:h-0.5 md:w-auto md:min-w-[14px] md:flex-1',
      )}
    >
      <span
        className="absolute inset-0 origin-top animate-draw-y bg-primary md:origin-left md:animate-draw-x"
        style={{ animationDelay: `${delayMs}ms` }}
      />
    </div>
  );
}

interface IconChipProps {
  icon: RoleIntroStep['icon'];
  good?: boolean;
  small?: boolean;
}

function IconChip({ icon: Icon, good, small }: IconChipProps): JSX.Element {
  return (
    <span
      className={cn(
        'flex flex-none items-center justify-center rounded-md border border-border shadow-sm',
        small ? 'h-12 w-12' : 'h-14 w-14',
        good === true ? 'text-good' : 'text-primary',
      )}
    >
      <Icon className={small ? 'h-[22px] w-[22px]' : 'h-6 w-6'} aria-hidden="true" />
    </span>
  );
}

function Beat({ beat, delayMs }: { beat: RoleIntroBeat; delayMs: number }): JSX.Element {
  return (
    <div
      className={cn(
        'flex items-center gap-3 text-left',
        // 8.125rem = the prototype's 130px beat. It may SHRINK on a narrow viewport (min-w-0)
        // so a six-beat role degrades by wrapping its labels rather than by scrolling.
        'md:w-[8.125rem] md:min-w-0',
        'md:flex-col md:items-center md:gap-2.5 md:text-center',
      )}
    >
      <span className="animate-pop opacity-0" style={{ animationDelay: `${delayMs}ms` }}>
        <IconChip icon={beat.icon} good={beat.good} />
      </span>
      <span className="flex flex-col gap-px">
        <span
          className="animate-rise text-[0.82rem] font-semibold leading-tight opacity-0"
          style={{ animationDelay: `${delayMs + 90}ms` }}
        >
          {beat.label}
        </span>
        {beat.sub !== undefined ? (
          <span className="text-[0.72rem] leading-tight text-muted-foreground">{beat.sub}</span>
        ) : null}
        {beat.expands === true ? (
          <span className="text-[0.66rem] font-bold text-primary">↓ {beat.hint ?? 'See how'}</span>
        ) : null}
      </span>
    </div>
  );
}

/**
 * A sibling BELOW the journey row, never a member of it (TF-0002). A lane wraps rather than
 * scrolling: the prototype's own `overflow-x: auto` silently cut a lane's last steps at tablet
 * width, and a step nobody can see is worse than a step on a second line.
 */
function ForkPanel({ fork, startDelayMs }: { fork: RoleIntroFork; startDelayMs: number }): JSX.Element {
  return (
    <div
      data-testid="role-intro-fork"
      className="animate-rise rounded-md border border-dashed border-border p-5 opacity-0"
      style={{ animationDelay: `${startDelayMs}ms` }}
    >
      <p className="mb-4 text-xs font-bold uppercase tracking-wide text-muted-foreground">
        {fork.title}
      </p>
      <div className="flex flex-col gap-[18px]">
        {fork.lanes.map((lane) => {
          let stepDelay = startDelayMs + 130;
          return (
            <div key={lane.name} className="flex flex-col items-start gap-2 sm:flex-row sm:gap-4">
              <p className="w-auto flex-none text-sm font-bold text-foreground sm:w-[7.5rem] sm:pt-[13px]">
                {lane.name}
              </p>
              <div className="flex flex-1 flex-wrap items-start gap-y-3 self-stretch">
                {lane.steps.map((step, i) => {
                  const chipDelay = stepDelay;
                  stepDelay += 85;
                  return (
                    // Connector and the step it FEEDS are one unwrappable unit, so a lane that
                    // has to wrap never leaves a line ending in a line pointing at nothing.
                    <div key={step.label} className="flex flex-none items-start">
                      {i > 0 ? (
                        <div className="relative mt-6 h-0.5 w-[22px] flex-none overflow-hidden bg-border">
                          <span
                            className="absolute inset-0 origin-left scale-x-0 animate-draw-x bg-primary"
                            style={{ animationDelay: `${chipDelay - 60}ms` }}
                          />
                        </div>
                      ) : null}
                      <div className="flex w-24 flex-none flex-col items-center gap-[7px] text-center">
                        <span
                          className="animate-pop opacity-0"
                          style={{ animationDelay: `${chipDelay}ms` }}
                        >
                          <IconChip icon={step.icon} good={step.good} small />
                        </span>
                        <span className="text-xs font-semibold leading-tight text-muted-foreground">
                          {step.label}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
